import type { DraftCompatibility, SchemaChange } from "../../shared/api/client";
import type { CompiledManifest } from "../../shared/api/generated/contracts";
import type { MessageKey } from "../../shared/i18n";
import { labelOfOption } from "../renderer/fields";

type ChangeGroup = "fields" | "views" | "print";

export interface ChangeLine {
  /** Stable React key. */
  key: string;
  group: ChangeGroup;
  messageKey: MessageKey;
  /** What the change is about, as the owner knows it (a label, never content). */
  subject: string;
  /** Second part: the new name, the previous value, the screen it belongs to. */
  detail?: string;
  /** Names of changed list settings, to be translated by the caller. */
  detailKeys?: MessageKey[];
}

const KIND_MESSAGES = {
  field_added: "draft.changes.field_added",
  field_hidden: "draft.changes.field_hidden",
  field_shown: "draft.changes.field_shown",
  label_changed: "draft.changes.label_changed",
  default_changed: "draft.changes.default_changed",
  required_changed: "draft.changes.required_on",
  option_added: "draft.changes.option_added",
  option_color_changed: "draft.changes.option_color_changed",
  collection_added: "draft.changes.collection_added",
  view_added: "draft.changes.view_added",
  layout_changed: "draft.changes.layout_changed",
  order_changed: "draft.changes.order_changed",
  list_config_changed: "draft.changes.list_config_changed",
  control_added: "draft.changes.control_added",
  control_removed: "draft.changes.control_removed",
  print_form_added: "draft.changes.print_form_added",
  print_form_changed: "draft.changes.print_form_changed",
  primary_field_changed: "draft.changes.primary_field_changed",
  other_changed: "draft.changes.other_changed",
} as const satisfies Record<string, MessageKey>;

/** Every change kind of ADR 0201 the server can send. */
export const SCHEMA_CHANGE_KINDS = Object.keys(KIND_MESSAGES);

const LIST_KEY_MESSAGES: Record<string, MessageKey> = {
  group_by: "draft.changes.listKey.group_by",
  sort_by: "draft.changes.listKey.sort_by",
  sort_dir: "draft.changes.listKey.sort_dir",
  badge_field_id: "draft.changes.listKey.badge_field_id",
  subtitle_field_id: "draft.changes.listKey.subtitle_field_id",
  empty_text: "draft.changes.listKey.empty_text",
  filters: "draft.changes.listKey.filters",
};

/** Message key of one list-setting name; `null` for a name the Mini App lacks. */
function listKeyMessage(key: string): MessageKey | null {
  return LIST_KEY_MESSAGES[key] ?? null;
}

interface Labels {
  collection: (id: string | null | undefined) => string | undefined;
  field: (
    collection: string | null | undefined,
    id: string | null | undefined,
  ) => string | undefined;
  view: (id: string | null | undefined) => string | undefined;
  printForm: (id: string | null | undefined) => string | undefined;
}

function labelsOf(manifest: CompiledManifest | undefined): Labels {
  const collections = manifest?.collections ?? [];
  return {
    collection: (id) => collections.find((item) => item.id === id)?.label,
    field: (collection, id) =>
      collections
        .filter((item) => collection == null || item.id === collection)
        .flatMap((item) => item.fields)
        .find((item) => item.id === id)?.label,
    view: (id) => manifest?.views.find((item) => item.id === id)?.label,
    printForm: (id) => {
      const form = manifest?.print_forms.find((item) => item.id === id);
      return typeof form?.label === "string" ? form.label : undefined;
    },
  };
}

function groupOf(change: SchemaChange): ChangeGroup {
  if (change.print_form_id || change.kind.startsWith("print_form"))
    return "print";
  if (
    change.view_id ||
    change.kind === "view_added" ||
    change.kind.startsWith("control_") ||
    change.kind === "layout_changed" ||
    change.kind === "list_config_changed"
  )
    return "views";
  if (change.kind === "order_changed")
    return change.path.startsWith("/collections") ? "fields" : "views";
  return "fields";
}

function firstText(...values: (string | null | undefined)[]): string {
  return (
    values.find(
      (value) => value !== undefined && value !== null && value !== "",
    ) ?? ""
  );
}

function arrow(before?: string | null, after?: string | null): string {
  return [before, after].filter((part) => part).join(" → ");
}

/** One change of the server list as a phrase key and the names to show with it. */
export function describeSchemaChange(
  change: SchemaChange,
  manifest?: CompiledManifest,
): ChangeLine {
  const labels = labelsOf(manifest);
  const kind = change.kind in KIND_MESSAGES ? change.kind : "other_changed";
  const messageKey: MessageKey =
    kind === "required_changed" && change.after === "false"
      ? "draft.changes.required_off"
      : KIND_MESSAGES[kind as keyof typeof KIND_MESSAGES];
  const field =
    labels.field(change.collection_id, change.field_id) ??
    change.field_id ??
    "";
  const view = labels.view(change.view_id) ?? change.view_id ?? "";
  const collection =
    labels.collection(change.collection_id) ?? change.collection_id ?? "";
  const printForm =
    labels.printForm(change.print_form_id) ?? change.print_form_id ?? "";
  let subject: string;
  let detail: string | undefined;
  let detailKeys: MessageKey[] | undefined;
  switch (kind) {
    case "field_added":
    case "field_shown":
      subject = change.after ?? field;
      break;
    case "field_hidden":
      subject = change.before ?? field;
      break;
    case "required_changed":
      subject = field;
      break;
    case "default_changed":
      subject = field;
      detail = arrow(change.before, change.after);
      break;
    case "label_changed":
      subject = firstText(change.before, field, view, collection, printForm);
      detail = change.after ?? undefined;
      break;
    case "option_added":
      subject = change.after ?? change.option_id ?? "";
      detail = field;
      break;
    case "option_color_changed": {
      const definition = manifest?.collections
        .filter((item) => item.id === change.collection_id)
        .flatMap((item) => item.fields)
        .find((item) => item.id === change.field_id);
      subject = definition
        ? labelOfOption(definition, change.option_id ?? "")
        : (change.option_id ?? "");
      detail = arrow(change.before, change.after);
      break;
    }
    case "collection_added":
      subject = change.after ?? collection;
      break;
    case "primary_field_changed":
      subject = collection;
      detail = arrow(change.before, change.after);
      break;
    case "view_added":
      subject = change.after ?? view;
      break;
    case "layout_changed":
      subject = view;
      detail = arrow(change.before, change.after);
      break;
    case "order_changed": {
      const [, root, id] =
        /^\/(collections|views)\/([^/]+)/.exec(change.path) ?? [];
      subject =
        root === "collections"
          ? (labels.collection(id) ?? id ?? "")
          : (labels.view(id) ?? id ?? "");
      break;
    }
    case "list_config_changed": {
      subject = view;
      const names = (change.after ?? "").split(",").filter(Boolean);
      detailKeys = names.flatMap((name) => listKeyMessage(name) ?? []);
      detail = names.filter((name) => listKeyMessage(name) === null).join(", ");
      break;
    }
    case "control_added":
    case "control_removed":
      subject = (kind === "control_added" ? change.after : change.before) ?? "";
      detail = view || undefined;
      break;
    case "print_form_added":
      subject = change.after ?? printForm;
      break;
    case "print_form_changed":
      subject = printForm;
      break;
    default:
      subject = printForm || view || collection || "";
  }
  return {
    key: `${change.kind}:${change.path}:${change.option_id ?? ""}:${change.after ?? ""}`,
    group: groupOf(change),
    messageKey,
    subject,
    detail: detail === "" ? undefined : detail,
    detailKeys: detailKeys?.length ? detailKeys : undefined,
  };
}

interface ChangeGroupLines {
  group: ChangeGroup;
  lines: ChangeLine[];
}

const GROUP_ORDER: ChangeGroup[] = ["fields", "views", "print"];

export function groupChanges(
  changes: readonly SchemaChange[],
  manifest?: CompiledManifest,
): ChangeGroupLines[] {
  const lines = changes.map((change) => describeSchemaChange(change, manifest));
  return GROUP_ORDER.map((group) => ({
    group,
    lines: lines.filter((line) => line.group === group),
  })).filter((entry) => entry.lines.length > 0);
}

export interface BackfillLine {
  field: string;
  value: string;
  records: number;
}

/** What publishing would write into existing records, field by field. */
export function backfillLines(
  compatibility: DraftCompatibility | null | undefined,
  manifest: CompiledManifest | undefined,
): BackfillLine[] {
  const labels = labelsOf(manifest);
  return (compatibility?.backfill ?? []).map((step) => {
    const definition = manifest?.collections
      .filter((item) => item.id === step.collection_id)
      .flatMap((item) => item.fields)
      .find((item) => item.id === step.field_id);
    const raw =
      typeof step.default === "string"
        ? step.default
        : JSON.stringify(step.default);
    return {
      field: labels.field(step.collection_id, step.field_id) ?? step.field_id,
      value:
        definition?.type === "select" && typeof step.default === "string"
          ? labelOfOption(definition, step.default)
          : raw,
      records: step.records,
    };
  });
}
